import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import CustomSelect from 'components/inputs/CustomSelect';
import SubmitButton from 'components/SubmitButton';
import {BillingRule} from 'types/billingRule';
import XIcon from 'assets/images/icons/XIcon';
import DateInput from 'components/inputs/DateInput';
import {BillingSolution} from 'types/billingSolution';
import {Payer} from 'types/payer';
import {Location} from 'types/location';
import {Service} from 'types/service';
import {setIsEditing} from '../services/servicesSlice';
import {ServicesService} from 'services/servicesService';
import {BillingSolutionService} from 'services/billingSolutionService';
import {LocationService} from 'services/locationService';
import {PayerService} from 'services/payerService';
import {BillingRulesThunks} from './billingRulesThunks';
import CurrencyInput from 'components/inputs/CurrencyInput';

interface BillingRulesDrawerProps {
  id?: string;
  closeDrawer: () => void;
}

const BillingRuleDrawer: React.FC<BillingRulesDrawerProps> = ({
  closeDrawer,
}) => {
  const billingRule = useSelector(
    (state: RootState) => state.billingRule.billingRule,
  );
  const dispatch = useDispatch<AppDispatch>();
  const [updatedBillingRule, setUpdatedBillingRule] = useState({
    ...billingRule,
  });
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [servicesOptions, setServicesOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [locationOptions, setLocationsOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [payersOptions, setPayersOptions] = useState<
    {label: string; value: string}[]
  >([]);
  const [billingSolutionOptions, setBillingSolutionOptions] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const isFormValid =
      updatedBillingRule.asOf && updatedBillingRule.billingSolutionId;
    setIsSaveDisabled(!isFormValid);
  }, [updatedBillingRule.asOf, updatedBillingRule.billingSolutionId]);

  useEffect(() => {
    setUpdatedBillingRule({...billingRule});
  }, [billingRule]);

  useEffect(() => {
    const fetchOptions = async () => {
      setIsLoading(true);
      try {
        const servicesResponse = await ServicesService.index(
          1,
          {where: {}},
          '',
          '',
          100,
        );
        const serviceOpts =
          servicesResponse.data?.map((service: Service) => ({
            label: service.name,
            value: service.id,
          })) || [];
        setServicesOptions(serviceOpts);
        const locationsResponse = await LocationService.index(
          1,
          {where: {}},
          '',
          '',
          100,
        );
        const locationOpts =
          locationsResponse.data?.map((location: Location) => ({
            label: location.name,
            value: location.id,
          })) || [];
        setLocationsOptions(locationOpts);
        const payersResponse = await PayerService.index(
          1,
          {where: {}},
          '',
          '',
          100,
        );
        const payerOpts =
          payersResponse.data?.map((payer: Payer) => ({
            label: payer.name,
            value: payer.id,
          })) || [];
        setPayersOptions(payerOpts);
        const solutionsResponse = await BillingSolutionService.index(
          1,
          {where: {}},
          '',
          '',
          100,
        );
        const solutionOpts =
          solutionsResponse.data?.map((solution: BillingSolution) => ({
            label: solution.name,
            value: solution.id,
          })) || [];
        setBillingSolutionOptions(solutionOpts);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const handleChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | {target: {id: string; value: any}},
  ) => {
    const {id, value} = e.target;
    setUpdatedBillingRule({...updatedBillingRule, [id]: value});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (billingRule?.id) {
      await dispatch(
        BillingRulesThunks.update(new BillingRule(updatedBillingRule)),
      ).then(() => closeDrawer());
    } else {
      await dispatch(
        BillingRulesThunks.create(new BillingRule(updatedBillingRule)),
      ).then(() => closeDrawer());
    }
  };

  const handleClose = () => {
    dispatch(setIsEditing(false));
    closeDrawer();
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="flex-1 flex flex-col">
        <div className="h-auto p-6 bg-sky-50 border-b border-sky-200 flex justify-between items-start gap-2 w-full">
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="text-lg font-semibold tracking-normal text-cyan-800">
              {billingRule?.id
                ? 'Edit Billing Rule'
                : 'Create New Billing Rule'}
            </div>
            <div className="text-sm font-light tracking-normal leading-5 text-zinc-400">
              {billingRule?.id ? 'Edit ' : 'Create a new '}billing rule and set
              its parameters
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none pt-2">
            <XIcon />
          </button>
        </div>
        <div className="flex-1 px-3 pb-6 pt-3 overflow-y-auto">
          <div className="h-full flex flex-col overflow-y-auto overflow-hidden">
            <div className="h-full px-4">
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col space-y-6 items-center mt-4">
                  {isLoading ? (
                    <div className="w-full text-center py-8">
                      <div className="text-gray-500">Loading options...</div>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col gap-[0.625rem] w-full">
                        <label
                          htmlFor="enableHeader"
                          className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                          Services
                        </label>
                        <CustomSelect
                          options={servicesOptions}
                          isMulti={true}
                          value={updatedBillingRule.serviceIds || ''}
                          onChange={selectedOptions => {
                            const selectedValues = selectedOptions.map(
                              (option: any) => option.value,
                            );
                            setUpdatedBillingRule((prevData: BillingRule) => ({
                              ...prevData,
                              serviceIds: selectedValues,
                            }));
                          }}
                        />
                      </div>
                      <div className="flex flex-col gap-[0.625rem] w-full">
                        <label
                          htmlFor="enableHeader"
                          className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                          Locations
                        </label>
                        <CustomSelect
                          options={locationOptions}
                          isMulti={true}
                          value={updatedBillingRule.locationIds || ''}
                          onChange={selectedOptions => {
                            const selectedValues = selectedOptions.map(
                              (option: any) => option.value,
                            );
                            setUpdatedBillingRule((prevData: BillingRule) => ({
                              ...prevData,
                              locationIds: selectedValues,
                            }));
                          }}
                        />
                      </div>
                      <div className="flex flex-col gap-[0.625rem] w-full">
                        <label
                          htmlFor="enableHeader"
                          className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                          Payers
                        </label>
                        <CustomSelect
                          options={payersOptions}
                          isMulti={true}
                          value={updatedBillingRule.payerIds || ''}
                          onChange={selectedOptions => {
                            const selectedValues = selectedOptions.map(
                              (option: any) => option.value,
                            );
                            setUpdatedBillingRule((prevData: BillingRule) => ({
                              ...prevData,
                              payerIds: selectedValues,
                            }));
                          }}
                        />
                      </div>
                      <div className="flex flex-col gap-[0.625rem] w-full">
                        <label
                          htmlFor="enableHeader"
                          className="w-full block text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                          Solutions
                        </label>
                        <CustomSelect
                          options={billingSolutionOptions}
                          value={updatedBillingRule.billingSolutionId || ''}
                          onChange={selectedOption =>
                            handleChange({
                              target: {
                                id: 'billingSolutionId',
                                value: selectedOption.value,
                              },
                            })
                          }
                        />
                      </div>
                      <div className="w-full">
                        <DateInput
                          type="date"
                          placeholder=""
                          id="asOf"
                          label="As Of"
                          value={updatedBillingRule.asOf || ''}
                          onChange={handleChange}
                          allowPastDates={true}
                        />
                      </div>
                      <div className="w-full">
                        <CurrencyInput
                          id="defaultRate"
                          label="Default Rate"
                          value={updatedBillingRule.defaultRateInCents}
                          onChange={cents => {
                            setUpdatedBillingRule({
                              ...updatedBillingRule,
                              defaultRateInCents: cents,
                            });
                          }}
                          placeholder="0.00"
                        />
                      </div>
                    </>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <footer className="flex-shrink-0 px-6 pb-4 pt-5 flex justify-end gap-5">
        <div className="border-t border-slate-200 w-full">
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <SubmitButton
              onClick={handleSubmit}
              label={
                billingRule?.id ? 'Save Billing Rule' : 'Create Billing Rule'
              }
              disabled={isSaveDisabled}
            />
          </div>
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <button
              type="submit"
              onClick={handleClose}
              className="self-center text-sm font-light tracking-normal leading-5 text-center text-zinc-400 hover:text-cyan-400">
              Never mind
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BillingRuleDrawer;
